import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export const ModalWrapper = ({
  title,
  children,
  isOpen,
  onClose,

  /* Footer Actions */
  showFooter = true,
  cancelLabel = "Cancel",
  primaryActionLabel = "Submit",
  primaryDisabled = false,
  onPrimaryAction,
  primaryLoading = false,

  allowEasyClose = true,
  maxWidth = "max-w-lg",
}) => {
  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);

  /* ESC CLOSE */
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  /* SCROLL LOCK */
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  /* AUTO FOCUS */
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        closeBtnRef.current?.focus();
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4
      bg-black/60 backdrop-blur-sm "
      onMouseDown={(e) => {
        if (allowEasyClose && e.target === e.currentTarget && !primaryLoading) {
          onClose();
        }
      }}
    >
      {/* ESC Hint */}
      {allowEasyClose && (
        <div className="absolute top-4 right-4  bg-black/80 px-3 py-1.5 text-xs text-white shadow">
          Press <kbd className="font-mono font-semibold">ESC</kbd> to close
        </div>
      )}

      {/* Modal Panel */}
      <div
        ref={modalRef}
        className={`relative w-full ${maxWidth}
        bg-surfaceColor  shadow-[0_20px_50px_rgba(0,0,0,0.25)]
        border border-borderLighto max-h-[70vh] my-auto
        flex flex-col overflow-hidden
         duration-200`}
      >
        {/* HEADER */}
        <div
          className="relative flex items-center justify-between px-6 py-5 border-b border-borderLight
        bg-gradient-to-r from-primaryAccent via-[#D63CC1] to-primaryAccentHover"
        >
          <h2 className="text-lg font-semibold tracking-wide text-white font-heading">
            {title}
          </h2>

          {/* CLOSE BUTTON */}
          <button
            ref={closeBtnRef}
            onClick={onClose}
            disabled={primaryLoading}
            className="
            group relative flex items-center justify-center
            w-9 h-9 
            bg-white/20 border border-white/30
            text-white
            transition-all duration-200
            hover:bg-white hover:text-primaryAccent
            hover:scale-105
            disabled:opacity-50
            "
          >
            <X
              size={18}
              className="transition-transform duration-200 group-hover:rotate-90"
            />
          </button>
        </div>

        {/* BODY */}
        <div className="overflow-y-auto px-6 py-6 bg-surfaceColor">
          {children}
        </div>

        {/* FOOTER */}
        {showFooter && (
          <div className="border-t border-borderLight px-6 py-4 bg-gray-50 flex justify-end gap-3">
            {/* Cancel */}
            <button
              onClick={onClose}
              disabled={primaryLoading}
              className="
              px-5 py-2  border border-borderLight
              text-headingText font-medium
              hover:bg-gray-200 text-sm
              transition active:scale-[0.97]
              disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {cancelLabel}
            </button>

            {/* Primary */}
            <button
              onClick={onPrimaryAction}
              disabled={primaryLoading || primaryDisabled}
              className="
              inline-flex items-center gap-2
              px-6 py-2 text-sm
              bg-btnColor text-white font-semibold
              hover:bg-btnHoverColor
              transition active:scale-[0.97]
              disabled:opacity-70 disabled:cursor-not-allowed
              "
            >
              {primaryLoading && (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}

              {primaryLoading ? `${primaryActionLabel}...` : primaryActionLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
